import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { Customer360Response, GrowthCrmService } from './growth-crm.service';
import {
  GrowthLoyaltyRulesForm,
  createDefaultLoyaltyRulesForm,
  createLoyaltyPointsRule,
  createLoyaltyTierRule,
  serializeLoyaltyRulesForm,
  validateLoyaltyRulesForm
} from './growth-loyalty-rule.models';

type Customer360Tab = 'overview' | 'leads' | 'activities' | 'loyalty';

@Component({
  standalone: true,
  selector: 'ain-growth-customer360',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent,
    FormsModule, TableModule, ButtonModule, DialogModule, InputTextModule, InputNumberModule,
    PartySelectorComponent,
    AinDatePipe
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.crm.customer360.title')"
      [description]="t('growth.crm.customer360.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.crm.customer360.help.page.title'"
        [descriptionKey]="'growth.crm.customer360.help.page.description'"
        [bulletKeys]="[
          'growth.crm.customer360.help.page.bullets.partyRef',
          'growth.crm.customer360.help.page.bullets.noDuplicate',
          'growth.crm.customer360.help.page.bullets.intelligence'
        ]" />

      <section class="card">
        <div class="section-title">
          <h3>{{ t('growth.crm.customer360.sections.lookup') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.crm.customer360.help.lookup.title'"
            [descriptionKey]="'growth.crm.customer360.help.lookup.description'"
            [bulletKeys]="['growth.crm.customer360.help.lookup.bullets.partySelector']" />
        </div>
        <div class="toolbar lookup-toolbar">
          <ain-form-field [label]="t('growth.crm.customer360.fields.party')">
            <ain-party-selector [(ngModel)]="partyId" />
          </ain-form-field>
          <div class="lookup-actions">
            <button pButton type="button" [label]="t('catalog.action.search')" (click)="lookup()"></button>
            <button pButton type="button" severity="secondary" [label]="t('growth.crm.actions.clearFilters')" (click)="clearLookup()"></button>
          </div>
        </div>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('growth.crm.customer360.loading')" />
      } @else if (notFound()) {
        <ain-state type="empty" [title]="t('growth.crm.customer360.notFound')" />
      } @else if (view()) {
        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'overview'" (click)="activeTab.set('overview')">{{ t('growth.crm.customer360.tabs.overview') }}</button>
            <button type="button" [class.active]="activeTab() === 'leads'" (click)="activeTab.set('leads')">{{ t('growth.crm.customer360.tabs.leads') }}</button>
            <button type="button" [class.active]="activeTab() === 'activities'" (click)="activeTab.set('activities')">{{ t('growth.crm.customer360.tabs.activities') }}</button>
            <button type="button" [class.active]="activeTab() === 'loyalty'" (click)="activeTab.set('loyalty')">{{ t('growth.crm.customer360.tabs.loyalty') }}</button>
          </div>

          @if (activeTab() === 'overview') {
            <div class="overview-grid">
              <div class="overview-card">
                <div class="section-title">
                  <h4>{{ t('growth.crm.customer360.sections.party') }}</h4>
                </div>
                <p>{{ view()!.party?.displayName ?? '-' }}</p>
                <p class="muted">{{ t('growth.crm.customer360.referenceOnly') }}</p>
              </div>
              <div class="overview-card">
                <div class="section-title">
                  <h4>{{ t('growth.crm.customer360.sections.context') }}</h4>
                </div>
                <p>{{ t('growth.dashboard.fields.lifecycleStage') }}: {{ view()!.growthContext?.lifecycleStage ?? '-' }}</p>
                <p>{{ t('growth.dashboard.fields.industry') }}: {{ view()!.growthContext?.primaryIndustryKey ?? '-' }}</p>
              </div>
              <div class="overview-card">
                <div class="section-title">
                  <h4>{{ t('growth.crm.customer360.sections.sales') }}</h4>
                  <ain-info-hint
                    [titleKey]="'growth.crm.customer360.help.sales.title'"
                    [descriptionKey]="'growth.crm.customer360.help.sales.description'"
                    [bulletKeys]="['growth.crm.customer360.help.sales.bullets.stub']" />
                </div>
                <p>{{ view()!.sales.note }}</p>
                <p>{{ t('growth.crm.customer360.salesMode') }}: {{ view()!.sales.mode }}</p>
              </div>
              <div class="overview-card">
                <div class="section-title">
                  <h4>{{ t('growth.crm.customer360.sections.intelligence') }}</h4>
                  <ain-info-hint
                    [titleKey]="'growth.crm.customer360.help.intelligence.title'"
                    [descriptionKey]="'growth.crm.customer360.help.intelligence.description'"
                    [bulletKeys]="['growth.crm.customer360.help.intelligence.bullets.eg6']" />
                </div>
                <p>{{ view()!.intelligence.availabilityNote }}</p>
              </div>
              @if (view()!.service) {
                <div class="overview-card">
                  <div class="section-title">
                    <h4>{{ t('growth.crm.customer360.sections.service') }}</h4>
                  </div>
                  <p>{{ t('growth.service.successPlanDetail.fields.healthScore') }}: {{ view()!.service!.healthScore ?? '-' }}</p>
                  <p>{{ t('growth.crm.customer360.service.openCaseLinks') }}: {{ view()!.service!.openCaseLinkCount }}</p>
                  <p>{{ t('growth.crm.customer360.service.openRetentionPlays') }}: {{ view()!.service!.openRetentionPlayCount }}</p>
                </div>
              }
            </div>
          }

          @if (activeTab() === 'leads') {
            @if (view()!.leads.length === 0) {
              <ain-state type="empty" [title]="t('growth.crm.customer360.leadsEmpty')" />
            } @else {
              <p-table [value]="view()!.leads">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.crm.leads.columns.leadNumber') }}</th>
                    <th>{{ t('growth.crm.leads.columns.status') }}</th>
                    <th>{{ t('growth.crm.leads.fields.contentAsset') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.leadNumber }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.contentAssetId ? t('growth.crm.leads.refs.linked') : '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }

          @if (activeTab() === 'activities') {
            @if (view()!.recentActivities.length === 0) {
              <ain-state type="empty" [title]="t('growth.crm.customer360.activitiesEmpty')" />
            } @else {
              <p-table [value]="view()!.recentActivities">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.crm.customer360.columns.activityType') }}</th>
                    <th>{{ t('growth.crm.customer360.columns.occurredAt') }}</th>
                    <th>{{ t('growth.crm.leads.fields.contentAsset') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.activityType }}</td>
                    <td>{{ row.occurredAtUtc | ainDate:'datetime' }}</td>
                    <td>{{ row.contentAssetId ? t('growth.crm.leads.refs.linked') : '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }

          @if (activeTab() === 'loyalty') {
            <div class="toolbar loyalty-toolbar">
              <button pButton type="button" [label]="t('growth.crm.loyalty.create')" (click)="openLoyaltyDialog()"></button>
            </div>
            @if (loyaltyPrograms().length === 0) {
              <ain-state type="empty" [title]="t('growth.crm.customer360.loyaltyEmpty')" />
            } @else {
              <p-table [value]="loyaltyPrograms()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.crm.loyalty.columns.key') }}</th>
                    <th>{{ t('growth.crm.loyalty.columns.name') }}</th>
                    <th>{{ t('growth.crm.loyalty.columns.status') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.programKey }}</td>
                    <td>{{ row.name }}</td>
                    <td>{{ row.status }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        </section>
      }

      <p-dialog
        [header]="t('growth.crm.loyalty.create')"
        [visible]="showLoyaltyDialog()"
        (visibleChange)="showLoyaltyDialog.set($event)"
        [modal]="true"
        [style]="{ width: 'min(720px, 96vw)' }">
        <div class="section-title">
          <ain-info-hint
            [titleKey]="'growth.crm.loyalty.help.rules.title'"
            [descriptionKey]="'growth.crm.loyalty.help.rules.description'"
            [bulletKeys]="['growth.crm.loyalty.help.rules.bullets.structured']" />
        </div>

        <div class="dialog-grid">
          <ain-form-field [label]="t('growth.crm.loyalty.fields.key')">
            <input pInputText [(ngModel)]="loyaltyModel.programKey" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.crm.loyalty.fields.name')">
            <input pInputText [(ngModel)]="loyaltyModel.name" />
          </ain-form-field>
        </div>

        <section class="rule-card">
          <div class="section-title">
            <h4>{{ t('growth.crm.loyalty.sections.tierRules') }}</h4>
            <button pButton type="button" severity="secondary" size="small" [label]="t('growth.crm.loyalty.actions.addTierRule')" (click)="addTierRule()"></button>
          </div>
          @if (loyaltyRulesForm.tierRules.length === 0) {
            <p class="muted">{{ t('growth.crm.loyalty.tierRulesEmpty') }}</p>
          }
          @for (tier of loyaltyRulesForm.tierRules; track tier.localId) {
            <div class="rule-row">
              <ain-form-field [label]="t('growth.crm.loyalty.fields.tierKey')">
                <input pInputText [(ngModel)]="tier.tierKey" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.crm.loyalty.fields.minPoints')">
                <p-inputNumber [(ngModel)]="tier.minPoints" [min]="0" [maxFractionDigits]="0" />
              </ain-form-field>
              <div class="rule-row-actions">
                <button pButton type="button" severity="danger" [label]="t('common.remove')" (click)="removeTierRule(tier.localId)"></button>
              </div>
            </div>
          }
        </section>

        <section class="rule-card">
          <div class="section-title">
            <h4>{{ t('growth.crm.loyalty.sections.pointsRules') }}</h4>
            <button pButton type="button" severity="secondary" size="small" [label]="t('growth.crm.loyalty.actions.addPointsRule')" (click)="addPointsRule()"></button>
          </div>
          @if (loyaltyRulesForm.pointsRules.length === 0) {
            <p class="muted">{{ t('growth.crm.loyalty.pointsRulesEmpty') }}</p>
          }
          @for (points of loyaltyRulesForm.pointsRules; track points.localId) {
            <div class="rule-row">
              <ain-form-field [label]="t('growth.crm.loyalty.fields.actionKey')">
                <input pInputText [(ngModel)]="points.actionKey" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.crm.loyalty.fields.points')">
                <p-inputNumber [(ngModel)]="points.points" [min]="0" [maxFractionDigits]="0" />
              </ain-form-field>
              <div class="rule-row-actions">
                <button pButton type="button" severity="danger" [label]="t('common.remove')" (click)="removePointsRule(points.localId)"></button>
              </div>
            </div>
          }
        </section>

        <div class="rule-actions">
          <button pButton type="button" severity="secondary" [text]="true" [label]="t('growth.crm.json.previewToggle')" (click)="showLoyaltyJsonPreview.set(!showLoyaltyJsonPreview())"></button>
        </div>

        @if (loyaltyValidationError()) {
          <p class="validation-error">{{ t(loyaltyValidationError()!) }}</p>
        }

        @if (showLoyaltyJsonPreview()) {
          <div class="json-preview">
            <span class="preview-label">{{ t('growth.crm.json.previewLabel') }}</span>
            <pre>{{ loyaltyJsonPreview() }}</pre>
          </div>
        }

        <ng-template pTemplate="footer">
          <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="showLoyaltyDialog.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="savingLoyalty()" (click)="createLoyaltyProgram()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3, .card h4 { margin: 0; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar, .lookup-actions, .loyalty-toolbar, .rule-actions, .rule-row-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: flex-end; }
    .lookup-toolbar { align-items: flex-end; }
    .lookup-toolbar ain-form-field { flex: 1; min-width: 260px; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
    .tabs button { border: 0; background: transparent; padding: 8px 12px; border-radius: 8px; cursor: pointer; }
    .tabs button.active { background: #ecfeff; color: #0f766e; font-weight: 600; }
    .overview-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
    .overview-card { border: 1px solid #f3f4f6; border-radius: 10px; padding: 10px; display: grid; gap: 6px; }
    .muted { color: #6b7280; font-size: 0.9rem; margin: 0; }
    .dialog-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .rule-card { border: 1px solid #f3f4f6; border-radius: 10px; padding: 12px; display: grid; gap: 10px; margin-top: 12px; }
    .rule-row { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); border-top: 1px dashed #e5e7eb; padding-top: 10px; }
    .validation-error { color: #b91c1c; margin: 0; }
    .json-preview { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; margin-top: 8px; }
    .json-preview pre { margin: 6px 0 0; white-space: pre-wrap; word-break: break-word; font-size: 12px; }
    .preview-label { font-size: 12px; color: #6b7280; font-weight: 600; }
  `]
})
export class GrowthCustomer360Component {
  private readonly crmService = inject(GrowthCrmService);
  private readonly localizationService = inject(LocalizationService);

  readonly loading = signal(false);
  readonly notFound = signal(false);
  readonly view = signal<Customer360Response | null>(null);
  readonly loyaltyPrograms = signal<{ programKey: string; name: string; status: string }[]>([]);
  readonly showLoyaltyDialog = signal(false);
  readonly showLoyaltyJsonPreview = signal(false);
  readonly savingLoyalty = signal(false);
  readonly loyaltyValidationError = signal<string | null>(null);
  readonly activeTab = signal<Customer360Tab>('overview');
  readonly loyaltyJsonPreview = computed(() => {
    const serialized = serializeLoyaltyRulesForm(this.loyaltyRulesForm);
    return JSON.stringify(serialized, null, 2);
  });

  partyId: string | null = null;
  loyaltyModel = { programKey: '', name: '' };
  loyaltyRulesForm: GrowthLoyaltyRulesForm = createDefaultLoyaltyRulesForm();

  lookup(): void {
    if (!this.partyId) return;
    this.loading.set(true);
    this.notFound.set(false);
    this.view.set(null);
    this.activeTab.set('overview');
    this.crmService.getCustomer360(this.partyId).subscribe({
      next: response => {
        this.view.set(response);
        this.loading.set(false);
        this.loadLoyaltyPrograms();
      },
      error: () => {
        this.notFound.set(true);
        this.loading.set(false);
      }
    });
  }

  clearLookup(): void {
    this.partyId = null;
    this.view.set(null);
    this.notFound.set(false);
    this.activeTab.set('overview');
  }

  openLoyaltyDialog(): void {
    this.loyaltyModel = { programKey: '', name: '' };
    this.loyaltyRulesForm = createDefaultLoyaltyRulesForm();
    this.loyaltyValidationError.set(null);
    this.showLoyaltyJsonPreview.set(false);
    this.showLoyaltyDialog.set(true);
  }

  addTierRule(): void {
    this.loyaltyRulesForm.tierRules = [...this.loyaltyRulesForm.tierRules, createLoyaltyTierRule()];
  }

  removeTierRule(localId: string): void {
    this.loyaltyRulesForm.tierRules = this.loyaltyRulesForm.tierRules.filter(item => item.localId !== localId);
  }

  addPointsRule(): void {
    this.loyaltyRulesForm.pointsRules = [...this.loyaltyRulesForm.pointsRules, createLoyaltyPointsRule()];
  }

  removePointsRule(localId: string): void {
    this.loyaltyRulesForm.pointsRules = this.loyaltyRulesForm.pointsRules.filter(item => item.localId !== localId);
  }

  loadLoyaltyPrograms(): void {
    this.crmService.listLoyaltyPrograms().subscribe({
      next: items => this.loyaltyPrograms.set(items.map(item => ({
        programKey: item.programKey,
        name: item.name,
        status: item.status
      })))
    });
  }

  createLoyaltyProgram(): void {
    if (!this.loyaltyModel.programKey.trim() || !this.loyaltyModel.name.trim()) {
      this.loyaltyValidationError.set('growth.crm.loyalty.ruleValidation.identityRequired');
      return;
    }

    const validationError = validateLoyaltyRulesForm(this.loyaltyRulesForm);
    if (validationError) {
      this.loyaltyValidationError.set(validationError);
      return;
    }

    this.loyaltyValidationError.set(null);
    this.savingLoyalty.set(true);
    const serialized = serializeLoyaltyRulesForm(this.loyaltyRulesForm);
    this.crmService.createLoyaltyProgram({
      programKey: this.loyaltyModel.programKey.trim(),
      name: this.loyaltyModel.name.trim(),
      tierRulesJson: serialized.tierRulesJson,
      pointsRulesJson: serialized.pointsRulesJson,
      status: 'Active'
    }).subscribe({
      next: () => {
        this.showLoyaltyDialog.set(false);
        this.savingLoyalty.set(false);
        this.loadLoyaltyPrograms();
      },
      error: () => {
        this.savingLoyalty.set(false);
        this.loyaltyValidationError.set('growth.crm.loyalty.ruleValidation.saveFailed');
      }
    });
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
